import { DataGrid, type DataGridColumn } from "@heroui-pro/react/data-grid";
import { EmptyState } from "@heroui-pro/react/empty-state";
import { Button, Skeleton, Typography } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useCallback, useMemo, useState } from "react";

import { STOCK_TABLE_LOOK } from "@/components/common/data-grid-look";
import { ConfirmDeleteModal } from "@/components/common/confirm-delete-modal";
import { EmptyDash } from "@/components/common/empty-row-value";
import { ListFooter } from "@/components/common/list-footer";
import { RowActionsMenu } from "@/components/common/row-actions-menu";
import { useDeleteVariantAttribute } from "@/hooks/api/variant-attributes";
import { useMemoizedCallback } from "@/hooks/useMemorizedCallback";
import { useRoles } from "@/hooks/useRoles";
import { VariantAttribute } from "@/types/product";
import { formatAbsoluteDate } from "@/utils/date";

type AttributeColumnKey = "name" | "options" | "createdAt";

const COLUMNS: Array<{ uid: AttributeColumnKey; name: string }> = [
  { uid: "name", name: "Name" },
  { uid: "options", name: "Options" },
  { uid: "createdAt", name: "Date added" },
];

const SKELETON_ROWS = Array.from(
  { length: 10 },
  (_, i) => ({ id: `skeleton-${i}` }) as VariantAttribute,
);

const SKELETON_ROW_IDS = SKELETON_ROWS.map((row) => row.id);

interface VariantAttributesTableProps {
  attributes: Array<VariantAttribute>;
  isFetching: boolean;
  search?: string;
  countLabel?: string;
  onEdit: (attribute: VariantAttribute) => void;
  onClearSearch: () => void;
}

export const VariantAttributesTable = ({
  attributes,
  isFetching,
  search,
  countLabel,
  onEdit,
  onClearSearch,
}: VariantAttributesTableProps) => {
  const [deleting, setDeleting] = useState<VariantAttribute | null>(null);

  const { can } = useRoles();
  const {
    mutate: deleteAttribute,
    isPending: isDeleting,
    error: deleteError,
    reset: resetDelete,
  } = useDeleteVariantAttribute();

  const canEdit = can("store.manage");
  const canDelete = canEdit && can("store.delete");
  const deleteTitle = `Delete ${deleting?.name ?? "attribute"}?`;

  const getCellContent = useCallback(
    (attribute: VariantAttribute, columnKey: AttributeColumnKey) => {
      switch (columnKey) {
        case "name":
          return (
            <div className="flex min-w-0 max-w-80 items-center gap-2">
              <Typography
                className="whitespace-nowrap hover:underline"
                type="body-sm"
                weight="medium"
              >
                {attribute.name}
              </Typography>
              {attribute.description && (
                <Typography
                  truncate
                  color="muted"
                  title={attribute.description}
                  type="body-sm"
                >
                  {attribute.description}
                </Typography>
              )}
            </div>
          );
        case "options": {
          const names = (attribute.options ?? [])
            .map((option) => option.name)
            .join(", ");

          return names ? (
            <Typography
              truncate
              className="block max-w-80"
              title={names}
              type="body-sm"
            >
              {names}
            </Typography>
          ) : (
            <EmptyDash />
          );
        }
        case "createdAt":
          return (
            <Typography color="muted" type="body-sm">
              {formatAbsoluteDate(attribute.createdAt)}
            </Typography>
          );
      }
    },
    [],
  );

  const handleDeleteOpen = useMemoizedCallback(
    (attribute: VariantAttribute) => {
      resetDelete();
      setDeleting(attribute);
    },
  );

  const columns = useMemo<DataGridColumn<VariantAttribute>[]>(() => {
    const dataColumns = COLUMNS.map(
      (column): DataGridColumn<VariantAttribute> => ({
        cell: (attribute) =>
          isFetching ? (
            <Skeleton className="h-3.5 w-24 rounded-md" />
          ) : (
            getCellContent(attribute, column.uid)
          ),
        header: column.name,
        id: column.uid,
        isRowHeader: column.uid === "name",
      }),
    );

    return [
      ...dataColumns,
      {
        align: "end",
        cell: (attribute) =>
          isFetching ? (
            <Skeleton className="ml-auto size-6 rounded-md" />
          ) : (
            <RowActionsMenu
              label="Attribute actions"
              onDelete={
                canDelete ? () => handleDeleteOpen(attribute) : undefined
              }
              onEdit={canEdit ? () => onEdit(attribute) : undefined}
            />
          ),
        header: <span className="sr-only">Actions</span>,
        id: "actions",
        width: 48,
      },
    ];
  }, [
    isFetching,
    getCellContent,
    canEdit,
    canDelete,
    handleDeleteOpen,
    onEdit,
  ]);

  const handleRowAction = useMemoizedCallback((key: string | number) => {
    if (isFetching || !canEdit) return;
    const attribute = attributes.find((item) => item.id === String(key));

    if (attribute) onEdit(attribute);
  });

  const handleDeleteConfirm = () => {
    if (!deleting) return;
    deleteAttribute(deleting.id, { onSuccess: () => setDeleting(null) });
  };

  const renderEmptyState = useMemoizedCallback(() => {
    const activeSearch = search?.trim();

    return (
      <EmptyState className="py-16">
        <EmptyState.Header>
          <EmptyState.Media className="text-muted">
            <Icon
              fontSize={24}
              icon={
                activeSearch ? "solar:magnifer-linear" : "solar:tuning-2-linear"
              }
            />
          </EmptyState.Media>
          <EmptyState.Title>
            {activeSearch ? "No matching attributes" : "No attributes yet"}
          </EmptyState.Title>
          <EmptyState.Description className="max-w-sm text-pretty">
            {activeSearch
              ? `Nothing matches “${activeSearch}”. Try a different name or clear the search.`
              : "Attributes like size and color let a product come in variants."}
          </EmptyState.Description>
        </EmptyState.Header>
        {activeSearch && (
          <EmptyState.Content>
            <Button size="sm" variant="outline" onPress={onClearSearch}>
              Clear search
            </Button>
          </EmptyState.Content>
        )}
      </EmptyState>
    );
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-hidden">
        <DataGrid
          aria-label="Variant attributes table"
          {...STOCK_TABLE_LOOK}
          columns={columns}
          data={isFetching ? SKELETON_ROWS : attributes}
          disabledKeys={isFetching ? SKELETON_ROW_IDS : undefined}
          getRowId={(attribute) => attribute.id}
          renderEmptyState={renderEmptyState}
          scrollContainerClassName="h-full min-h-0 overflow-auto"
          onRowAction={handleRowAction}
        />
      </div>
      {countLabel && <ListFooter label={countLabel} />}
      <ConfirmDeleteModal
        description="Products using this attribute will lose their values. You can't undo this."
        error={deleteError}
        isOpen={deleting !== null}
        isPending={isDeleting}
        title={deleteTitle}
        onClose={() => setDeleting(null)}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};
